/* Design tokens of dubai-todo.com (Handoff board of the design canvas) and the
   page basics. Keep the colors in sync with src/constants/theme.ts. The shared
   components (@todo/web-ui) read every color from here — a site defines the
   whole set (apps/miami/src/global.css is the other). */

:root {
  --background: #faf6f0;
  --card: #ffffff;
  --band: #f6efe5; /* the category strip, empty-state notes, image placeholders */
  --footer-base: #f3ebe0;
  --border: #eae1d4;
  --border-strong: #e1d7c9;
  --text: #1e1a16;
  --text-secondary: #5e564d;
  --text-muted: #6e655b;
  --text-body: #3e3730; /* card lines, detail copy */
  --eyebrow: #4a423a; /* section eyebrows */
  --text-rgb: 30, 26, 22; /* --text, for the menu backdrop */
  --ink-rgb: 20, 12, 6; /* the shade under photo captions */
  --shadow-rgb: 60, 35, 15;
  --primary: #7a5434;
  --primary-hover: #694529;
  --primary-rgb: 122, 84, 52; /* --primary, for focus rings */
  --brand: #8a5a35;
  --label: #6b4a2e;
  --brand-ink: #4a2f1b; /* the logo's "DUBAI — TO DO —" (riviera: its navy) */
  --brand-gold: #c7a35c; /* the logo's sun and rules — shared with riviera-todo.com */
  --accent: #c7a35c; /* article list markers, link underlines, quote rules */
  --footer-rule: #e1d3c0;

  --font-display: 'Cormorant Garamond', Georgia, 'Times New Roman', serif;
  --font-body: Montserrat, system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif;

  /* The hero (@todo/web-ui hero-search): the photo's stand-in colour, the
     cream veil over it, the headline's size (desktop; phones: one line, see
     its CSS) and the lead's type. */
  --hero-fallback: #e9b98a;
  --veil-rgb: 250, 246, 240;
  --hero-title-size: 96px;
  --hero-title-phone-max: 38px;
  --hero-title-em: 11;
  --hero-title-phone-wrap: nowrap;
  --hero-lead-font: var(--font-display);
  --hero-lead-weight: 600;
  --hero-lead-size: 27px;
  --hero-lead-size-phone: 18px;

  /* The header: the hero's hazy sky as it shows under the hero's cream veil —
     on phones the veil covers the hero's top, so a pale wash; on desktop it
     fades out to the right, so cream on the left and the sky's sand on the
     right (the media query below). No bottom border: the sky runs into the
     hero, as on miami. */
  --header-bg: linear-gradient(90deg, #faf6f0 0%, #f5ede3 55%, #efe3d5 100%);
  --header-border: none;
  /* On the home page the header lies over the hero's palms: a cream glow keeps
     the icons legible. */
  --header-icon-halo: drop-shadow(0 0 1.5px #faf6f0) drop-shadow(0 0 3px #faf6f0)
    drop-shadow(0 0 8px rgba(250, 246, 240, 0.85));

  /* Desktop footer columns start right of the photo's palm leaf. */
  --footer-inset: clamp(24px, 18vw, 300px);
  --mood-columns: 5;
  --mood-label-size: 25px;
  --mood-label-size-phone: 22px;

  --radius-pill: 999px;
  --radius-card: 4px;
  --shadow-card: 0 2px 14px rgba(60, 35, 15, 0.07);
  --shadow-search: 0 10px 30px rgba(60, 35, 15, 0.18);

  /* Desktop container 1320 px, 40 px side padding; 20 px on mobile. */
  --container: 1320px;
  --gutter: 20px;
}

@media (min-width: 1024px) {
  :root {
    --gutter: 40px;
    --header-bg: linear-gradient(90deg, #f8f2ea 0%, #f1e6d9 45%, #e8d7c6 80%, #e3d0bd 100%);
  }
}

html,
body {
  background: var(--background);
  color: var(--text);
  font-family: var(--font-body);
  -webkit-font-smoothing: antialiased;
}

a {
  color: inherit;
  text-decoration: none;
}

a:hover {
  color: var(--primary);
}

:focus-visible {
  outline: 2px solid var(--primary);
  outline-offset: 2px;
}

.visually-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
}
